import axios, { AxiosInstance, AxiosError, AxiosRequestConfig } from 'axios';
import { ORDER_TYPE_STORE_COUNTER } from '../Utils/Constants/Service';
import { TokenManager } from './TokenManager';

// Extensão da interface para incluir a propriedade _retry
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean; // Propriedade opcional
}

export default class BaseService {
  orderType: string;
  token?: string | null;
  guestToken?: string | null;
  http: AxiosInstance;

  constructor(orderType: string) {
    this.orderType = orderType;
    this.token = TokenManager.getToken();
    this.guestToken = TokenManager.getGuestToken();
    this.http = this.createApiInstance();

    this.http.interceptors.response.use(
      response => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as CustomAxiosRequestConfig;

        // Verifica se o erro é devido a token expirado
        if (error.response && error.response.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          if (!originalRequest.headers) {
            originalRequest.headers = {};
          }

          try {
            if (this.token) {
              const newToken = await this.refreshToken(this.token).catch((resp) => {
                if (resp?.response?.data?.error === 'could_not_refresh_token') {
                  TokenManager.setTokenErrorRemoveLogin('true');
                }
              });
              if (newToken) {
                this.token = newToken;
                TokenManager.setToken(newToken);
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                return this.http(originalRequest);
              }
            }

          } catch (refreshError: any) {
            if (refreshError?.response?.data?.error === 'could_not_refresh_token') {
              TokenManager.setTokenErrorRemoveLogin('true');
            }
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    )
  }

  createApiInstance(): AxiosInstance {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (this.guestToken) {
      headers['Guest-Token'] = `${this.guestToken}`;
    }

    const lang = localStorage.getItem('i18nextLng');
    if (lang) {
      headers['Accept-Language'] = lang;
    }

    return axios.create({
      baseURL: this.getApiBaseUrl(),
      headers: headers,
    });
  }

  getApiBaseUrl(): string {
    switch (this.orderType) {
      case ORDER_TYPE_STORE_COUNTER:
        return process.env.REACT_APP_API_URL_STORE_COUNTER!;
      default:
        return process.env.REACT_APP_API_URL!;
    }
  }

  async refreshToken(token: string): Promise<string | null> {
    try {
      const response = await this.http.post('/auth/refresh', {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response?.data?.token ?? null;
    } catch (error) {
      console.error("Erro ao atualizar token:", error);
      throw error;
    }
  }

  async refreshGuestToken(token: string): Promise<string> {
    const response = await this.http.post('/auth/refresh', {}, {
      headers: {
        'Guest-Token': `${token}`
      }
    });
    return response?.data?.token;
  }

}
